import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { isPlayerApp } from '../lib/appSurface';
import { useInstall } from '../lib/pwa/install';
import { useBrandName } from '../lib/pwa/brand';
import { InstallInstructionsModal } from './InstallInstructionsModal';
import { showSuccessToast } from '../lib/toast';

// Story 065 — Install banner shown to logged-in players on the player surface.
// Hidden when the app is already installed, when the user dismissed the
// banner, or on auth pages where the banner would compete with the auth UI.

const HIDE_ON_PATHS = new Set([
  '/login',
  '/register',
  '/auth/callback',
  '/auth/confirm',
]);

export function InstallAppBanner() {
  const { t } = useTranslation('common');
  const { user, loading } = useAuth();
  const { installed, dismissed, touchPrimary, install, dismissBanner } =
    useInstall();
  const brand = useBrandName();
  const { pathname } = useLocation();
  const [modalOpen, setModalOpen] = useState(false);

  if (!isPlayerApp()) return null;
  if (loading || !user) return null;
  if (installed) return null;
  if (dismissed) return null;
  if (HIDE_ON_PATHS.has(pathname)) return null;
  // Story 065 followup — only push install on touch-primary devices.
  // Desktop users have the browser's address-bar install icon and
  // don't need our affordance taking up space.
  if (!touchPrimary) return null;

  const handleInstall = async () => {
    const outcome = await install();
    if (outcome === 'instructions-modal') {
      setModalOpen(true);
    } else if (outcome === 'accepted') {
      // Confirm the install. Chrome's own "Open" infobar handles
      // launching the standalone app — we just acknowledge here.
      showSuccessToast(t('header.install.installed_toast', { brand }));
    }
    // 'dismissed' = the user closed the native dialog without installing;
    // no toast.
  };

  return (
    <>
      <div
        data-testid="install-app-banner"
        className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
      >
        <div className="text-sm">
          {t('install_banner.body_prefix')}
          <span className="font-semibold">{brand}</span>
          {t('install_banner.body_suffix')}
        </div>
        <div className="flex gap-2 items-center">
          <Button
            data-testid="install-app-banner-install"
            size="sm"
            variant="secondary"
            onClick={handleInstall}
          >
            {t('install_banner.install')}
          </Button>
          <Button
            data-testid="install-app-banner-dismiss"
            size="sm"
            variant="ghost"
            className="text-white hover:bg-slate-800"
            onClick={dismissBanner}
          >
            {t('install_banner.dismiss')}
          </Button>
        </div>
      </div>
      <InstallInstructionsModal
        open={modalOpen}
        brand={brand}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
